import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Compass, Menu, Phone, X, MessageCircle } from "lucide-react";
import { NAV_LINKS, PHONE, PHONE_LINK, WHATSAPP_LINK } from "../data/content";
import { scrollToSection } from "../lib/motion";
import { t } from "../i18n";
import LanguageToggle from "./LanguageToggle";

const Logo = ({ onClick }) => (
  <button onClick={onClick} className="flex items-center gap-2.5 group" data-testid="brand-logo" aria-label="The Trip World home">
    <span className="flex h-10 w-10 items-center justify-center rounded-full border border-gold/40 bg-gold/10 transition-transform duration-500 group-hover:rotate-45">
      <Compass className="h-5 w-5 text-gold" />
    </span>
    <span className="font-display font-extrabold tracking-tight text-lg sm:text-xl text-white leading-none text-left">
      THE TRIP <span className="text-gold">WORLD</span>
    </span>
  </button>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const go = (to) => (e) => {
    e.preventDefault();
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") navigate("/", { state: { scrollTo: id } });
      else scrollToSection(id);
    } else {
      navigate(to);
    }
    setOpen(false);
  };

  return (
    <>
      <header
        data-testid="sticky-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "glass-dark shadow-[0_8px_40px_-12px_rgba(2,6,23,0.9)]" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[72px]">
            <Logo onClick={go("/")} />
            <nav className="hidden xl:flex items-center gap-5" data-testid="desktop-nav">
              {NAV_LINKS.slice(1).map((l) => (
                <a
                  key={l.key}
                  href={l.to}
                  onClick={go(l.to)}
                  data-testid={`nav-${l.key}`}
                  className="whitespace-nowrap text-[12px] font-medium tracking-wide text-slate-300 hover:text-gold transition-colors duration-300"
                >
                  {t(`nav.${l.key}`)}
                </a>
              ))}
            </nav>
            <div className="hidden xl:flex items-center gap-4">
              <LanguageToggle compact />
              <a
                href={PHONE_LINK}
                data-testid="header-phone"
                className="flex items-center gap-2 text-sm font-semibold text-slate-200 hover:text-gold transition-colors whitespace-nowrap"
              >
                <Phone className="h-4 w-4 text-gold" />
                {PHONE}
              </a>
              <button
                onClick={go("/#enquiry")}
                data-testid="header-consultation-btn"
                className="btn-gold rounded-full px-6 py-2.5 text-[13px] font-bold tracking-wide font-display whitespace-nowrap"
              >
                {t("nav.consultation")}
              </button>
            </div>
            <button
              className="xl:hidden text-white p-2"
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open-btn"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-navy-deep/98 backdrop-blur-xl flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between h-[72px] px-4 sm:px-6">
              <Logo onClick={go("/")} />
              <button onClick={() => setOpen(false)} className="text-white p-2" data-testid="mobile-menu-close-btn" aria-label="Close menu">
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-8 py-8 flex flex-col gap-1">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.key}
                  href={l.to}
                  onClick={go(l.to)}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                  data-testid={`mobile-nav-${l.key}`}
                  className="font-display text-2xl font-bold text-slate-200 hover:text-gold py-2.5 border-b border-white/5 transition-colors"
                >
                  {t(`nav.${l.key}`)}
                </motion.a>
              ))}
            </nav>
            <div className="px-8 pb-4 flex justify-center">
              <LanguageToggle />
            </div>
            <div className="px-8 pb-10 flex gap-3">
              <a
                href={PHONE_LINK}
                data-testid="mobile-call-btn"
                className="flex-1 flex items-center justify-center gap-2 rounded-full btn-gold py-3.5 text-sm font-bold font-display"
              >
                <Phone className="h-4 w-4" /> {t("nav.call")}
              </a>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                data-testid="mobile-whatsapp-btn"
                className="flex-1 flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-navy-deep py-3.5 text-sm font-bold font-display"
              >
                <MessageCircle className="h-4 w-4" /> {t("nav.whatsapp")}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
